/**
 * Reporting Dashboard — Team analytics view.
 *
 * Shows aggregate metrics, daily hours chart, timekeeper table,
 * and top clients/matters breakdown. All data from reporting_entries cache.
 * Read-only — no editing. Click a timekeeper to scope the dashboard to that attorney.
 */

import {
	Building2,
	Check,
	ChevronsUpDown,
	Download,
	FolderOpen,
	Users,
	X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { toast } from "sonner";
import {
	type ReportingEntry,
	useReportingEntriesCache,
	useReportsCache,
} from "@/shared/cache";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/shared/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/shared/components/ui/popover";
import { cn } from "@/shared/lib/utils";
import { DatePicker } from "@/v2/components/date-picker/DatePicker";
import { getSelectedStatusValues } from "../components/Sidebar/statusFilters";
import { getReportingEntryStatus } from "../hooks/reportingStatus";
import {
	DATE_PRESET_LABELS,
	type DatePreset,
	getDateRange,
	type MemberRow,
	useReportingDashboard,
} from "../hooks/useReportingDashboard";

// Chart colors (brand palette)
const COLORS = {
	exported: "var(--chart-1)",
	tracked: "var(--chart-2)",
	deleted: "var(--chart-3)",
};

const CHART_COLORS = {
	grid: "var(--border)",
	axis: "var(--muted-foreground)",
	cursor: "color-mix(in srgb, var(--foreground) 6%, transparent)",
};

function fmtDate(d: Date): string {
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ─────────────────────────────────────────────
// Stat Card
// ─────────────────────────────────────────────

function StatCard({
	label,
	value,
	unit,
	subtitle,
	gradient,
}: {
	label: string;
	value: string;
	unit?: string;
	subtitle?: string;
	gradient: string;
}) {
	return (
		<div
			className={`relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br ${gradient} p-4 min-w-[130px]`}
		>
			<div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
			<p className="text-xs font-medium text-white/70 mb-1">{label}</p>
			<p className="text-2xl font-bold text-white tabular-nums">
				{value}
				{unit && (
					<span className="text-sm font-normal text-white/70 ml-1">{unit}</span>
				)}
			</p>
			{subtitle && <p className="text-xs text-white/50 mt-0.5">{subtitle}</p>}
		</div>
	);
}

// ─────────────────────────────────────────────
// Custom Tooltip
// ─────────────────────────────────────────────

function ChartTooltip({ active, payload, label }: any) {
	if (!active || !payload?.length) return null;
	const exported =
		payload.find((p: any) => p.dataKey === "exported")?.value ?? 0;
	const tracked = payload.find((p: any) => p.dataKey === "tracked")?.value ?? 0;
	const deleted = payload.find((p: any) => p.dataKey === "deleted")?.value ?? 0;
	return (
		<div className="rounded-lg border bg-background dark:bg-card p-3 shadow-md text-sm">
			<p className="font-medium text-foreground mb-1">
				{payload[0]?.payload?.tooltipLabel ?? label}
			</p>
			<div className="flex items-center gap-2">
				<span
					className="h-2 w-2 rounded-full"
					style={{ backgroundColor: COLORS.exported }}
				/>
				<span className="text-muted-foreground">Exported:</span>
				<span className="font-medium">{exported.toFixed(1)}h</span>
			</div>
			<div className="flex items-center gap-2">
				<span
					className="h-2 w-2 rounded-full"
					style={{ backgroundColor: COLORS.tracked }}
				/>
				<span className="text-muted-foreground">Tracked:</span>
				<span className="font-medium">{tracked.toFixed(1)}h</span>
			</div>
			<div className="flex items-center gap-2">
				<span
					className="h-2 w-2 rounded-full"
					style={{ backgroundColor: COLORS.deleted }}
				/>
				<span className="text-muted-foreground">Deleted:</span>
				<span className="font-medium">{deleted.toFixed(1)}h</span>
			</div>
			<div className="border-t mt-1 pt-1 flex items-center gap-2">
				<span className="text-muted-foreground">Total:</span>
				<span className="font-semibold">
					{(exported + tracked + deleted).toFixed(1)}h
				</span>
			</div>
		</div>
	);
}

// ─────────────────────────────────────────────
// CSV Export Helpers
// ─────────────────────────────────────────────

type CsvCell = boolean | number | string | null | undefined;

type CsvColumn = {
	header: string;
	value: (entry: ReportingEntry) => CsvCell;
};

const CSV_COLUMNS: CsvColumn[] = [
	{ header: "Transaction ID", value: (entry) => entry.id },
	{ header: "Professional ID", value: (entry) => entry.prof_id },
	{ header: "Attorney", value: (entry) => entry.prof_name },
	{ header: "Status", value: (entry) => getEntryStatus(entry) },
	{ header: "Date", value: (entry) => formatEntryDate(entry.date) },
	{ header: "Start Time", value: (entry) => entry.start_time },
	{ header: "End Time", value: (entry) => entry.end_time },
	{ header: "Hours", value: (entry) => Math.round(entry.units * 100) / 100 },
	{ header: "Client", value: (entry) => entry.client },
	{ header: "Matter Number", value: (entry) => entry.matter_number },
	{ header: "Matter Description", value: (entry) => entry.matter_description },
	{ header: "Task", value: (entry) => entry.task },
	{ header: "Activity", value: (entry) => entry.activity },
	{ header: "Application", value: (entry) => entry.application },
	{ header: "Description", value: (entry) => entry.description },
];

function getEntryStatus(entry: ReportingEntry): string {
	return getReportingEntryStatus(entry);
}

function formatEntryDate(date: string | null | undefined): string {
	return date?.split("T")[0] ?? "";
}

function escapeCsvCell(value: CsvCell): string {
	if (value === null || value === undefined) return "";
	const str = String(value);
	if (/[",\r\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
	return str;
}

function buildReportingEntriesCsv(entries: ReportingEntry[]): string {
	const sortedEntries = [...entries].sort((a, b) => {
		return (
			formatEntryDate(a.date).localeCompare(formatEntryDate(b.date)) ||
			(a.prof_name || "").localeCompare(b.prof_name || "") ||
			(a.start_time || "").localeCompare(b.start_time || "") ||
			a.id - b.id
		);
	});

	const rows = [
		CSV_COLUMNS.map((column) => escapeCsvCell(column.header)).join(","),
		...sortedEntries.map((entry) =>
			CSV_COLUMNS.map((column) => escapeCsvCell(column.value(entry))).join(","),
		),
	];

	return rows.join("\n");
}

function downloadCsv(filename: string, csvContent: string) {
	const blob = new Blob(["\ufeff", csvContent], {
		type: "text/csv;charset=utf-8;",
	});
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────
// Stacked Bar Shape (rounds top corners of topmost visible segment)
// ─────────────────────────────────────────────

const STACK_RADIUS = 4;

function RoundedTopBar(props: any) {
	const { x, y, width, height, fill } = props;
	if (!height || height <= 0) return null;
	const r = Math.min(STACK_RADIUS, height / 2, width / 2);
	return (
		<path
			d={`M${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + height} L${x},${y + height} Z`}
			fill={fill}
		/>
	);
}

function FlatBar(props: any) {
	const { x, y, width, height, fill } = props;
	if (!height || height <= 0) return null;
	return <rect x={x} y={y} width={width} height={height} fill={fill} />;
}

// ─────────────────────────────────────────────
// Sortable Table Header
// ─────────────────────────────────────────────

function SortableHeader({
	label,
	sortKey,
	currentSort,
	currentDir,
	onSort,
}: {
	label: string;
	sortKey: string;
	currentSort: string;
	currentDir: "asc" | "desc";
	onSort: (key: string) => void;
}) {
	const active = currentSort === sortKey;
	return (
		<th
			className="px-3 py-2 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none"
			onClick={() => onSort(sortKey)}
		>
			{label}
			{active && (
				<span className="ml-1">{currentDir === "asc" ? "↑" : "↓"}</span>
			)}
		</th>
	);
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

export default function ReportingDashboardView() {
	const [searchParams, setSearchParams] = useSearchParams();
	const { reports, isLoading: reportsLoading } = useReportsCache();

	// URL param helpers
	const setParams = useCallback(
		(updates: Record<string, string | null>) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					for (const [key, value] of Object.entries(updates)) {
						if (!value) next.delete(key);
						else next.set(key, value);
					}
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	// Data for scope selector
	const { userProfId } = useReportingEntriesCache();
	const hasDirectReports = reports.manages.length > 0;

	// URL-driven filters — scope: null=default, "me", "team", or a prof_id
	const scopeParam = searchParams.get("attorney") || null;
	// Default: "team" if has team, "me" if solo
	const effectiveScope = scopeParam || (hasDirectReports ? "team" : "me");
	const selectedStatuses = getSelectedStatusValues(searchParams.toString());
	const clientFilter = searchParams.get("client") || null;
	const matterFilter = searchParams.get("matter") || null;

	// Date range from URL (from= and to=), defaulting to last 30 days
	const dateRange = useMemo(() => {
		const fromStr = searchParams.get("from");
		const toStr = searchParams.get("to");
		if (fromStr && toStr) {
			const [fy, fm, fd] = fromStr.split("-").map(Number);
			const [ty, tm, td] = toStr.split("-").map(Number);
			return { from: new Date(fy, fm - 1, fd), to: new Date(ty, tm - 1, td) };
		}
		return getDateRange("last-30");
	}, [searchParams]);

	// Detect which preset matches the current range (for highlighting)
	const activePreset = useMemo((): DatePreset | null => {
		const fmt = (d: Date) =>
			`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
		const fromKey = fmt(dateRange.from);
		const toKey = fmt(dateRange.to);
		for (const key of Object.keys(DATE_PRESET_LABELS) as DatePreset[]) {
			const r = getDateRange(key);
			if (fmt(r.from) === fromKey && fmt(r.to) === toKey) return key;
		}
		return null;
	}, [dateRange]);

	const setDateRange = useCallback(
		(from: Date, to: Date) => {
			setParams({ from: fmtDate(from), to: fmtDate(to) });
		},
		[setParams],
	);

	// Selector states
	const [attorneyOpen, setAttorneyOpen] = useState(false);
	const [attorneySearch, setAttorneySearch] = useState("");
	const [clientOpen, setClientOpen] = useState(false);
	const [clientSearch, setClientSearch] = useState("");
	const [matterOpen, setMatterOpen] = useState(false);
	const [matterSearch, setMatterSearch] = useState("");

	// Table sort
	const [tableSort, setTableSort] = useState<string>("total");
	const [tableSortDir, setTableSortDir] = useState<"asc" | "desc">("desc");

	const handleSort = useCallback(
		(key: string) => {
			if (tableSort === key) {
				setTableSortDir((d) => (d === "asc" ? "desc" : "asc"));
			} else {
				setTableSort(key);
				setTableSortDir("desc");
			}
		},
		[tableSort],
	);

	// Dashboard data
	const dashboard = useReportingDashboard({
		dateRange,
		scope: effectiveScope,
		userProfId,
		statuses: selectedStatuses,
		client: clientFilter,
		matter: matterFilter,
	});

	const {
		stats,
		filteredEntries,
		dailyChart,
		memberTable,
		topClients,
		topMatters,
		availableClients,
		availableMatters,
	} = dashboard;

	// Scope selector filtered options
	const attorneyOptions = useMemo(() => {
		if (!attorneySearch) return reports.manages;
		const term = attorneySearch.toLowerCase();
		return reports.manages.filter(
			(p) =>
				p.full_name.toLowerCase().includes(term) ||
				p.email.toLowerCase().includes(term),
		);
	}, [reports.manages, attorneySearch]);

	// Scope label for button and viewing indicator
	const scopeLabel = useMemo(() => {
		if (effectiveScope === "me") return "Me";
		if (effectiveScope === "team") return "My Team";
		const member = reports.manages.find((p) => p.prof_id === effectiveScope);
		return member?.full_name || "Unknown";
	}, [effectiveScope, reports.manages]);

	const showScopeInViewingLabel =
		effectiveScope !== "team" && effectiveScope !== "me" && hasDirectReports;

	// Client selector filtered options
	const clientOptions = useMemo(() => {
		if (!clientSearch) return availableClients;
		const term = clientSearch.toLowerCase();
		return availableClients.filter((c) => c.toLowerCase().includes(term));
	}, [availableClients, clientSearch]);

	// Matter selector filtered options (scoped to selected client)
	const matterOptions = useMemo(() => {
		if (!matterSearch) return availableMatters;
		const term = matterSearch.toLowerCase();
		return availableMatters.filter(
			(m) =>
				m.matterNumber.toLowerCase().includes(term) ||
				m.matterDescription.toLowerCase().includes(term),
		);
	}, [availableMatters, matterSearch]);

	const selectedMatterLabel = matterFilter
		? availableMatters.find((m) => m.matterId === matterFilter)
		: null;

	// Sort member table
	const sortedMembers = useMemo(() => {
		const mult = tableSortDir === "asc" ? 1 : -1;
		return [...memberTable].sort((a, b) => {
			const av = a[tableSort as keyof MemberRow] ?? 0;
			const bv = b[tableSort as keyof MemberRow] ?? 0;
			if (typeof av === "string" && typeof bv === "string")
				return mult * av.localeCompare(bv);
			return mult * ((av as number) - (bv as number));
		});
	}, [memberTable, tableSort, tableSortDir]);

	const memberTotals = useMemo(
		() => ({
			tracked: memberTable.reduce((s, m) => s + m.tracked, 0),
			exported: memberTable.reduce((s, m) => s + m.exported, 0),
			deleted: memberTable.reduce((s, m) => s + m.deleted, 0),
			total: memberTable.reduce((s, m) => s + m.total, 0),
			entries: memberTable.reduce((s, m) => s + m.entries, 0),
		}),
		[memberTable],
	);

	const handleExportCsv = useCallback(() => {
		if (filteredEntries.length === 0) {
			toast.error("No entries to export", {
				description: "Adjust the reporting filters and try again.",
			});
			return;
		}

		const from = fmtDate(dateRange.from);
		const to = fmtDate(dateRange.to);
		downloadCsv(
			`reporting_entries_${from}_to_${to}.csv`,
			buildReportingEntriesCsv(filteredEntries),
		);
		toast.success("CSV exported", {
			description: `${filteredEntries.length} ${
				filteredEntries.length === 1 ? "entry" : "entries"
			} downloaded`,
		});
	}, [dateRange.from, dateRange.to, filteredEntries]);

	if (reportsLoading || dashboard.isLoading) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-muted-foreground">Loading dashboard...</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col h-full bg-background overflow-y-auto">
			{/* Filter bar */}
			<div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 px-6 py-4 bg-background border-b border-border">
				{/* Scope selector — hidden if user has no team (they only see their own data) */}
				{hasDirectReports && (
					<Popover open={attorneyOpen} onOpenChange={setAttorneyOpen}>
						<PopoverTrigger asChild>
							<button
								type="button"
								className="flex h-10 items-center gap-2 rounded-md border px-3 text-sm hover:bg-muted transition-colors"
							>
								<Users className="h-3.5 w-3.5 text-muted-foreground" />
								{scopeLabel}
								<ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
							</button>
						</PopoverTrigger>
						<PopoverContent className="w-[280px] p-0" align="start">
							<Command>
								<CommandInput
									placeholder="Search..."
									value={attorneySearch}
									onValueChange={setAttorneySearch}
								/>
								<CommandList>
									<CommandEmpty>No timekeepers found.</CommandEmpty>
									<CommandGroup>
										<CommandItem
											value="me"
											onSelect={() => {
												setParams({ attorney: "me" });
												setAttorneyOpen(false);
												setAttorneySearch("");
											}}
										>
											<Check
												className={cn(
													"mr-2 h-4 w-4",
													effectiveScope === "me" ? "opacity-100" : "opacity-0",
												)}
											/>
											<span className="font-medium">Me</span>
										</CommandItem>
										<CommandItem
											value="my-team"
											onSelect={() => {
												setParams({ attorney: "team" });
												setAttorneyOpen(false);
												setAttorneySearch("");
											}}
										>
											<Check
												className={cn(
													"mr-2 h-4 w-4",
													effectiveScope === "team"
														? "opacity-100"
														: "opacity-0",
												)}
											/>
											<span className="font-medium">My Team</span>
										</CommandItem>
									</CommandGroup>
									<CommandGroup heading="Timekeepers">
										{attorneyOptions.map((p) => (
											<CommandItem
												key={p.prof_id}
												value={`${p.full_name} ${p.email}`}
												onSelect={() => {
													setParams({ attorney: p.prof_id });
													setAttorneyOpen(false);
													setAttorneySearch("");
												}}
											>
												<Check
													className={cn(
														"mr-2 h-4 w-4",
														effectiveScope === p.prof_id
															? "opacity-100"
															: "opacity-0",
													)}
												/>
												<div className="flex flex-col">
													<span>{p.full_name}</span>
													<span className="text-xs text-muted-foreground">
														{p.prof_type || p.email}
													</span>
												</div>
											</CommandItem>
										))}
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>
				)}

				{/* Date range */}
				<DatePicker
					className="w-fit"
					dateRange={dateRange}
					onDateChange={(day) => setDateRange(day, day)}
					onDateRangeChange={(range) => {
						setDateRange(range.from, range.to);
					}}
					selectionMode="range"
					sideSlot={({ selectRange }) => (
						<div className="flex flex-col border-r p-1 w-[160px]">
							{Object.entries(DATE_PRESET_LABELS).map(([key, presetLabel]) => {
								const range = getDateRange(key as DatePreset);

								return (
									<button
										key={key}
										type="button"
										className={cn(
											"flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-muted text-left",
											activePreset === key && "bg-muted font-medium",
										)}
										onClick={() => selectRange(range)}
									>
										<Check
											className={cn(
												"h-3.5 w-3.5 shrink-0",
												activePreset === key ? "opacity-100" : "opacity-0",
											)}
										/>
										{presetLabel}
									</button>
								);
							})}
						</div>
					)}
				/>

				{/* Client filter */}
				<Popover open={clientOpen} onOpenChange={setClientOpen}>
					<PopoverTrigger asChild>
						<button
							type="button"
							className="flex h-10 items-center gap-1.5 rounded-md border px-3 text-sm hover:bg-muted transition-colors"
						>
							<Building2 className="h-3.5 w-3.5 text-muted-foreground" />
							{clientFilter || "All Clients"}
							<ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
						</button>
					</PopoverTrigger>
					<PopoverContent className="w-[240px] p-0" align="start">
						<Command>
							<CommandInput
								placeholder="Search clients..."
								value={clientSearch}
								onValueChange={setClientSearch}
							/>
							<CommandList>
								<CommandEmpty>No clients found.</CommandEmpty>
								<CommandGroup>
									<CommandItem
										onSelect={() => {
											setParams({ client: null, matter: null });
											setClientOpen(false);
											setClientSearch("");
										}}
									>
										<Check
											className={cn(
												"mr-2 h-4 w-4",
												!clientFilter ? "opacity-100" : "opacity-0",
											)}
										/>
										All Clients
									</CommandItem>
									{clientOptions.map((c) => (
										<CommandItem
											key={c}
											value={c}
											onSelect={() => {
												setParams({ client: c, matter: null });
												setClientOpen(false);
												setClientSearch("");
											}}
										>
											<Check
												className={cn(
													"mr-2 h-4 w-4",
													clientFilter === c ? "opacity-100" : "opacity-0",
												)}
											/>
											{c}
										</CommandItem>
									))}
								</CommandGroup>
							</CommandList>
						</Command>
					</PopoverContent>
				</Popover>

				{/* Matter filter */}
				<Popover open={matterOpen} onOpenChange={setMatterOpen}>
					<PopoverTrigger asChild>
						<button
							type="button"
							className="flex h-10 items-center gap-1.5 rounded-md border px-3 text-sm hover:bg-muted transition-colors max-w-[200px]"
						>
							<FolderOpen className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
							<span className="truncate">
								{selectedMatterLabel
									? selectedMatterLabel.matterNumber
									: "All Matters"}
							</span>
							<ChevronsUpDown className="h-3 w-3 text-muted-foreground shrink-0" />
						</button>
					</PopoverTrigger>
					<PopoverContent className="w-[300px] p-0" align="start">
						<Command>
							<CommandInput
								placeholder="Search matters..."
								value={matterSearch}
								onValueChange={setMatterSearch}
							/>
							<CommandList>
								<CommandEmpty>No matters found.</CommandEmpty>
								<CommandGroup>
									<CommandItem
										onSelect={() => {
											setParams({ matter: null });
											setMatterOpen(false);
											setMatterSearch("");
										}}
									>
										<Check
											className={cn(
												"mr-2 h-4 w-4",
												!matterFilter ? "opacity-100" : "opacity-0",
											)}
										/>
										All Matters
									</CommandItem>
									{matterOptions.map((m) => (
										<CommandItem
											key={m.matterId}
											value={`${m.matterNumber} ${m.matterDescription}`}
											onSelect={() => {
												// Auto-fill client when selecting a matter
												setParams({ matter: m.matterId, client: m.client });
												setMatterOpen(false);
												setMatterSearch("");
											}}
										>
											<Check
												className={cn(
													"mr-2 h-4 w-4 shrink-0",
													matterFilter === m.matterId
														? "opacity-100"
														: "opacity-0",
												)}
											/>
											<div className="flex flex-col min-w-0">
												<span className="truncate text-sm">
													{m.matterNumber}
												</span>
												<span className="text-xs text-muted-foreground truncate">
													{m.matterDescription} · {m.client}
												</span>
											</div>
										</CommandItem>
									))}
								</CommandGroup>
							</CommandList>
						</Command>
					</PopoverContent>
				</Popover>

				{/* Clear filters */}
				{(scopeParam ||
					clientFilter ||
					matterFilter ||
					searchParams.has("from")) && (
					<button
						type="button"
						className="flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
						onClick={() =>
							setParams({
								attorney: null,
								client: null,
								matter: null,
								status: null,
								from: null,
								to: null,
							})
						}
					>
						<X className="h-3 w-3" />
						Clear
					</button>
				)}

				{/* Spacer + export action (right-aligned) */}
				<div className="ml-auto flex items-center gap-3">
					{showScopeInViewingLabel && (
						<span className="text-sm text-muted-foreground">
							Viewing:{" "}
							<span className="font-medium text-foreground">{scopeLabel}</span>
						</span>
					)}
					<button
						type="button"
						className="flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-medium text-foreground hover:bg-muted transition-colors"
						onClick={handleExportCsv}
					>
						<Download className="size-3.5 text-muted-foreground" />
						Export CSV
					</button>
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 p-6 space-y-6">
				{/* Stat cards */}
				<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
					<StatCard
						label="Total Hours"
						value={stats.totalHours.toFixed(1)}
						unit="hrs"
						subtitle={`${stats.entryCount} entries`}
						gradient="from-[var(--brand-dark-teal)] to-[var(--brand-dark-green)]"
					/>
					<StatCard
						label="Exported"
						value={stats.exportedHours.toFixed(1)}
						unit="hrs"
						gradient="from-[var(--brand-600)] to-[var(--brand-900)]"
					/>
					<StatCard
						label="To Export"
						value={stats.trackedHours.toFixed(1)}
						unit="hrs"
						gradient="from-[var(--brand-500)] to-[var(--brand-700)]"
					/>
					<StatCard
						label="Deleted"
						value={stats.deletedHours.toFixed(1)}
						unit="hrs"
						gradient="from-[var(--brand-dark-slate)] to-[var(--brand-mid-slate)]"
					/>
					<StatCard
						label="Matters"
						value={String(stats.matterCount)}
						gradient="from-[var(--brand-900)] to-[var(--brand-950)]"
					/>
					<StatCard
						label="Avg / Day"
						value={stats.avgPerDay.toFixed(1)}
						unit="hrs"
						subtitle="weekdays"
						gradient="from-[var(--brand-dark-slate)] to-[var(--brand-mid-slate)]"
					/>
				</div>

				{/* Daily chart */}
				<div className="rounded-lg border bg-background dark:bg-card p-4 shadow-xs">
					<h3 className="text-sm font-medium text-foreground mb-4">
						Hours by Day
					</h3>

					<ResponsiveContainer width="100%" height={300}>
						<BarChart
							data={dailyChart}
							margin={{ top: 5, right: 5, bottom: 20, left: 0 }}
						>
							<CartesianGrid
								strokeDasharray="3 3"
								stroke={CHART_COLORS.grid}
								vertical={false}
							/>
							<XAxis
								dataKey="dateLabel"
								tick={{ fontSize: 11, fill: CHART_COLORS.axis }}
								tickLine={false}
								axisLine={false}
								angle={-35}
								textAnchor="end"
								interval={dailyChart.length > 40 ? 6 : 0}
							/>
							<YAxis
								tick={{ fontSize: 11, fill: CHART_COLORS.axis }}
								tickLine={false}
								axisLine={false}
								width={35}
							/>
							<Tooltip
								content={<ChartTooltip />}
								cursor={{ fill: CHART_COLORS.cursor }}
							/>
							<Legend
								iconType="circle"
								iconSize={8}
								wrapperStyle={{ fontSize: 12 }}
							/>
							<Bar
								dataKey="exported"
								stackId="a"
								fill={COLORS.exported}
								name="Exported"
								shape={(props: any) => {
									const entry = dailyChart[props.index];
									return entry && !entry.tracked && !entry.deleted ? (
										<RoundedTopBar {...props} />
									) : (
										<FlatBar {...props} />
									);
								}}
							/>
							<Bar
								dataKey="tracked"
								stackId="a"
								fill={COLORS.tracked}
								name="Tracked"
								shape={(props: any) => {
									const entry = dailyChart[props.index];
									return entry && !entry.deleted ? (
										<RoundedTopBar {...props} />
									) : (
										<FlatBar {...props} />
									);
								}}
							/>
							<Bar
								dataKey="deleted"
								stackId="a"
								fill={COLORS.deleted}
								name="Deleted"
								shape={RoundedTopBar}
							/>
						</BarChart>
					</ResponsiveContainer>
				</div>

				{/* Clients (left) + Matters (right) */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					{/* Clients */}
					<div className="rounded-lg border bg-background dark:bg-card shadow-xs overflow-hidden">
						<div className="px-4 py-3 border-b">
							<h3 className="text-sm font-medium text-foreground">Clients</h3>
						</div>
						<div className="overflow-y-auto min-h-[120px] max-h-[360px] p-4">
							{topClients.length > 0 ? (
								<div className="space-y-2">
									{topClients.map((c) => (
										<button
											type="button"
											key={c.client}
											className="flex items-center gap-3 w-full text-left rounded-md px-2 py-1.5 hover:bg-muted/50 transition-colors cursor-pointer"
											onClick={() =>
												setParams({ client: c.client, matter: null })
											}
										>
											<div className="flex-1 min-w-0">
												<div className="text-sm font-medium truncate">
													{c.client}
												</div>
												<div className="text-xs text-muted-foreground">
													{c.entries} entries
												</div>
											</div>
											<div className="text-sm font-medium tabular-nums w-16 text-right">
												{c.hours.toFixed(1)}h
											</div>
											<div className="w-24 h-2 rounded-full bg-muted overflow-hidden">
												<div
													className="h-full rounded-full"
													style={{
														width: `${Math.min(100, (c.hours / (topClients[0]?.hours || 1)) * 100)}%`,
														backgroundColor: COLORS.exported,
													}}
												/>
											</div>
										</button>
									))}
								</div>
							) : (
								<p className="text-sm text-muted-foreground text-center py-8">
									No client data for this period
								</p>
							)}
						</div>
					</div>

					{/* Matters */}
					<div className="rounded-lg border bg-background dark:bg-card shadow-xs overflow-hidden">
						<div className="px-4 py-3 border-b">
							<h3 className="text-sm font-medium text-foreground">Matters</h3>
						</div>
						<div className="overflow-y-auto min-h-[120px] max-h-[360px] p-4">
							{topMatters.length > 0 ? (
								<div className="space-y-2">
									{topMatters.map((m) => (
										<button
											type="button"
											key={m.matterId}
											className="flex items-center gap-3 w-full text-left rounded-md px-2 py-1.5 hover:bg-muted/50 transition-colors cursor-pointer"
											onClick={() =>
												setParams({ matter: m.matterId, client: m.client })
											}
										>
											<div className="flex-1 min-w-0">
												<div className="text-sm font-medium truncate">
													{m.matterNumber || m.matterId}
												</div>
												<div className="text-xs text-muted-foreground truncate">
													{m.matterDescription} · {m.client}
												</div>
											</div>
											<div className="text-sm font-medium tabular-nums w-16 text-right">
												{m.hours.toFixed(1)}h
											</div>
											<div className="w-24 h-2 rounded-full bg-muted overflow-hidden">
												<div
													className="h-full rounded-full"
													style={{
														width: `${Math.min(100, (m.hours / (topMatters[0]?.hours || 1)) * 100)}%`,
														backgroundColor: COLORS.exported,
													}}
												/>
											</div>
										</button>
									))}
								</div>
							) : (
								<p className="text-sm text-muted-foreground text-center py-8">
									No matter data for this period
								</p>
							)}
						</div>
					</div>
				</div>

				{/* Timekeepers — full width */}
				{effectiveScope === "team" && (
					<div className="rounded-lg border bg-background dark:bg-card shadow-xs">
						<div className="px-4 py-3 border-b">
							<h3 className="text-sm font-medium text-foreground">
								Timekeepers
							</h3>
						</div>
						<div>
							<table className="w-full text-sm">
								<thead className="sticky top-[65px] z-[5]">
									<tr className="border-b bg-muted shadow-[0_1px_0_0_var(--border)]">
										<SortableHeader
											label="Name"
											sortKey="profName"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="Tracked"
											sortKey="tracked"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="Exported"
											sortKey="exported"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="Deleted"
											sortKey="deleted"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="Total"
											sortKey="total"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="Entries"
											sortKey="entries"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="Matters"
											sortKey="matters"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
										<SortableHeader
											label="Avg/Day"
											sortKey="avgPerDay"
											currentSort={tableSort}
											currentDir={tableSortDir}
											onSort={handleSort}
										/>
									</tr>
								</thead>
								<tbody>
									{sortedMembers.map((m) => (
										<tr
											key={m.profId}
											className="border-b hover:bg-muted/20 cursor-pointer transition-colors"
											onClick={() => {
												setParams({ attorney: m.profId });
											}}
										>
											<td className="px-3 py-2">
												<div className="font-medium text-foreground">
													{m.profName}
												</div>
												{m.profType && (
													<div className="text-xs text-muted-foreground">
														{m.profType}
													</div>
												)}
											</td>
											<td className="px-3 py-2 text-muted-foreground">
												{m.tracked.toFixed(1)}
											</td>
											<td className="px-3 py-2 text-muted-foreground">
												{m.exported.toFixed(1)}
											</td>
											<td className="px-3 py-2 text-muted-foreground">
												{m.deleted.toFixed(1)}
											</td>
											<td className="px-3 py-2 font-medium">
												{m.total.toFixed(1)}
											</td>
											<td className="px-3 py-2 text-muted-foreground">
												{m.entries}
											</td>
											<td className="px-3 py-2 text-muted-foreground">
												{m.matters}
											</td>
											<td className="px-3 py-2 text-muted-foreground">
												{m.avgPerDay.toFixed(1)}
											</td>
										</tr>
									))}
								</tbody>
								<tfoot className="sticky bottom-0 z-[5]">
									<tr className="bg-muted font-medium border-t shadow-[0_-1px_0_0_var(--border)] [&>td:first-child]:rounded-bl-lg [&>td:last-child]:rounded-br-lg">
										<td className="px-3 py-2">Total</td>
										<td className="px-3 py-2">
											{memberTotals.tracked.toFixed(1)}
										</td>
										<td className="px-3 py-2">
											{memberTotals.exported.toFixed(1)}
										</td>
										<td className="px-3 py-2">
											{memberTotals.deleted.toFixed(1)}
										</td>
										<td className="px-3 py-2">
											{memberTotals.total.toFixed(1)}
										</td>
										<td className="px-3 py-2">{memberTotals.entries}</td>
										<td className="px-3 py-2" />
										<td className="px-3 py-2" />
									</tr>
								</tfoot>
							</table>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
